import {
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_MANUAL_VALUES = 100;

const isEmptyMetricValue = (value) =>
  value === null || value === undefined || String(value).trim() === '';

const hasValidCalendarDate = (value) => {
  if (!ISO_DATE_PATTERN.test(value ?? '')) return false;

  const [year, month, day] = value.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
};

const pluralize = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

export const getActiveDashboardMetrics = (metrics = []) =>
  [...metrics]
    .filter(
      (metric) =>
        !metric.fieldSource &&
        metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
    )
    .sort((left, right) => left.displayOrder - right.displayOrder);

export const getDashboardMetricStep = (metric) => {
  if (metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER) return 1;

  const decimalPlaces = Math.min(
    6,
    Math.max(0, Number(metric.decimalPlaces) || 0)
  );
  return 10 ** -decimalPlaces;
};

export const getDashboardMetricConstraintText = (metric) => {
  const constraints = [];
  const minimum = metric.minimum;
  const maximum = metric.maximum;

  if (minimum !== null && minimum !== undefined) {
    constraints.push(`Minimum ${minimum}`);
  }
  if (maximum !== null && maximum !== undefined) {
    constraints.push(`Maximum ${maximum}`);
  }

  if (metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER) {
    constraints.push('Whole numbers only');
  } else {
    const decimalPlaces = Math.min(
      6,
      Math.max(0, Number(metric.decimalPlaces) || 0)
    );
    constraints.push(
      decimalPlaces === 0
        ? 'No decimal places'
        : `Up to ${decimalPlaces} decimal ${
            decimalPlaces === 1 ? 'place' : 'places'
          }`
    );
  }

  return constraints.join(' · ');
};

export const validateDashboardMetricInput = (metric, rawValue) => {
  if (isEmptyMetricValue(rawValue)) {
    return metric.isRequired ? `${metric.label} is required.` : null;
  }

  const value = Number(rawValue);
  if (!Number.isFinite(value)) return 'Enter a valid number.';

  const decimalPlaces =
    metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER
      ? 0
      : Math.min(6, Math.max(0, Number(metric.decimalPlaces) || 0));
  const rounded = Number(value.toFixed(decimalPlaces));
  const tolerance = Number.EPSILON * Math.max(1, Math.abs(value)) * 4;

  if (
    (metric.valueType === DASHBOARD_VALUE_TYPE.INTEGER &&
      !Number.isInteger(value)) ||
    Math.abs(value - rounded) > tolerance
  ) {
    return decimalPlaces === 0
      ? 'Enter a whole number.'
      : `Enter no more than ${decimalPlaces} decimal ${
          decimalPlaces === 1 ? 'place' : 'places'
        }.`;
  }

  const minimum =
    metric.minimum === null || metric.minimum === undefined
      ? null
      : Number(metric.minimum);
  const maximum =
    metric.maximum === null || metric.maximum === undefined
      ? null
      : Number(metric.maximum);

  if (minimum !== null && value < minimum) {
    return `Enter a value of at least ${minimum}.`;
  }
  if (maximum !== null && value > maximum) {
    return `Enter a value no greater than ${maximum}.`;
  }

  return null;
};

export const validateDashboardSubmission = ({
  effectiveDate,
  fieldValues = {},
  metrics = [],
  maximumEffectiveDate = null
}) => {
  let effectiveDateError = null;
  if (!effectiveDate) {
    effectiveDateError = 'Effective date is required.';
  } else if (!hasValidCalendarDate(effectiveDate)) {
    effectiveDateError = 'Enter a valid effective date.';
  } else if (maximumEffectiveDate && effectiveDate > maximumEffectiveDate) {
    effectiveDateError = 'Effective date cannot be in the future.';
  }

  const fieldErrors = {};
  const values = [];

  getActiveDashboardMetrics(metrics).forEach((metric) => {
    const rawValue = fieldValues[metric.metricDefinitionId];
    const error = validateDashboardMetricInput(metric, rawValue);
    if (error) fieldErrors[metric.metricDefinitionId] = error;

    if (!isEmptyMetricValue(rawValue) && !error) {
      values.push({
        metricDefinitionId: metric.metricDefinitionId,
        value: Number(rawValue)
      });
    }
  });

  let formError = null;
  if (values.length === 0 && Object.keys(fieldErrors).length === 0) {
    formError = 'Enter at least one metric value.';
  } else if (values.length > MAX_MANUAL_VALUES) {
    formError = `A manual submission cannot exceed ${MAX_MANUAL_VALUES} metric values.`;
  }

  return {
    effectiveDateError,
    fieldErrors,
    formError,
    isValid:
      !effectiveDateError &&
      Object.keys(fieldErrors).length === 0 &&
      !formError,
    values
  };
};

export const getDashboardValueStatusLabel = (status) => {
  const labels = {
    approved: 'Approved',
    pending_approval: 'Awaiting approval',
    rejected: 'Rejected',
    superseded: 'Superseded',
    deleted: 'Deleted'
  };

  return labels[status] ?? 'Submitted';
};

export const buildDashboardSubmissionMessage = (summary = {}) => {
  const total = Number(summary.total) || 0;
  const approved = Number(summary.approved) || 0;
  const pendingApproval = Number(summary.pendingApproval) || 0;

  if (approved > 0 && pendingApproval > 0) {
    return `${pluralize(total, 'value')} submitted: ${pluralize(
      approved,
      'value'
    )} approved immediately and ${pluralize(
      pendingApproval,
      'value'
    )} awaiting approval.`;
  }
  if (pendingApproval > 0) {
    return `${pluralize(
      pendingApproval,
      'value'
    )} awaiting approval before appearing on the dashboard.`;
  }
  if (approved > 0) {
    return `${pluralize(
      approved,
      'value'
    )} approved immediately and available on the dashboard.`;
  }

  return `${pluralize(total, 'value')} submitted successfully.`;
};
